import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { useShallow } from 'zustand/react/shallow'

import type { SharePointDrive, SharePointSite } from '@/hooks/sharepoint/types'

import type { UUID } from '@/types/Id'

const STORE_NAME = 'sweetspot:sharepoint-location-history'
const MAX_RECENT_LOCATIONS_PER_ORG = 20

export type SharepointSavedLocationType = 'site' | 'drive'

export interface SharepointSavedLocation {
  driveId?: string
  driveName?: string
  driveWebUrl?: string
  key: string
  lastVisitedAt: number
  pinned: boolean
  siteId: string
  siteName: string
  siteWebUrl: string
  type: SharepointSavedLocationType
}

interface SharepointLocationHistoryState {
  locationsByOrg: Record<string, SharepointSavedLocation[]>
  recordDriveVisit: (
    orgId: UUID | '',
    site: SharePointSite,
    drive: SharePointDrive,
  ) => void
  recordSiteVisit: (orgId: UUID | '', site: SharePointSite) => void
  togglePinnedLocation: (
    orgId: UUID | '',
    location: SharepointSavedLocation,
  ) => void
}

const getOrganizationKey = (orgId: UUID | '') => orgId || 'unknown'

const getSiteLocationKey = (siteId: string) => `site:${siteId}`

const getDriveLocationKey = (siteId: string, driveId: string) =>
  `drive:${siteId}:${driveId}`

const createSiteLocation = (site: SharePointSite): SharepointSavedLocation => ({
  key: getSiteLocationKey(site.id),
  lastVisitedAt: Date.now(),
  pinned: false,
  siteId: site.id,
  siteName: site.name || 'Unnamed site',
  siteWebUrl: site.webUrl,
  type: 'site',
})

const createDriveLocation = (
  site: SharePointSite,
  drive: SharePointDrive,
): SharepointSavedLocation => ({
  driveId: drive.id,
  driveName: drive.name,
  driveWebUrl: drive.web_url,
  key: getDriveLocationKey(site.id, drive.id),
  lastVisitedAt: Date.now(),
  pinned: false,
  siteId: site.id,
  siteName: site.name || 'Unnamed site',
  siteWebUrl: site.webUrl,
  type: 'drive',
})

const upsertLocation = (
  locations: SharepointSavedLocation[],
  nextLocation: SharepointSavedLocation,
  options: {
    preserveExistingPinned?: boolean
  } = {},
) => {
  const { preserveExistingPinned = false } = options
  const existingLocation = locations.find(
    (location) => location.key === nextLocation.key,
  )
  const mergedLocation = existingLocation
    ? {
        ...existingLocation,
        ...nextLocation,
        pinned: preserveExistingPinned
          ? existingLocation.pinned
          : nextLocation.pinned,
      }
    : nextLocation

  const nextLocations = [
    mergedLocation,
    ...locations.filter((location) => location.key !== nextLocation.key),
  ]

  const pinnedLocations = nextLocations.filter((location) => location.pinned)
  const unpinnedLocations = nextLocations
    .filter((location) => !location.pinned)
    .slice(0, MAX_RECENT_LOCATIONS_PER_ORG)

  return [...pinnedLocations, ...unpinnedLocations]
}

export const useSharepointLocationHistoryStore =
  create<SharepointLocationHistoryState>()(
    persist(
      (set) => ({
        locationsByOrg: {},
        recordSiteVisit: (orgId, site) =>
          set((state) => {
            const orgKey = getOrganizationKey(orgId)
            const currentLocations = state.locationsByOrg[orgKey] ?? []
            return {
              locationsByOrg: {
                ...state.locationsByOrg,
                [orgKey]: upsertLocation(
                  currentLocations,
                  createSiteLocation(site),
                  { preserveExistingPinned: true },
                ),
              },
            }
          }),
        recordDriveVisit: (orgId, site, drive) =>
          set((state) => {
            const orgKey = getOrganizationKey(orgId)
            const currentLocations = state.locationsByOrg[orgKey] ?? []
            return {
              locationsByOrg: {
                ...state.locationsByOrg,
                [orgKey]: upsertLocation(
                  currentLocations,
                  createDriveLocation(site, drive),
                  { preserveExistingPinned: true },
                ),
              },
            }
          }),
        togglePinnedLocation: (orgId, location) =>
          set((state) => {
            if (location.type !== 'site') {
              return state
            }

            const orgKey = getOrganizationKey(orgId)
            const currentLocations = state.locationsByOrg[orgKey] ?? []
            const existingLocation = currentLocations.find(
              (entry) => entry.key === location.key,
            )
            const nextLocation = existingLocation
              ? {
                  ...existingLocation,
                  pinned: !existingLocation.pinned,
                }
              : {
                  ...location,
                  pinned: true,
                }

            return {
              locationsByOrg: {
                ...state.locationsByOrg,
                [orgKey]: upsertLocation(currentLocations, nextLocation),
              },
            }
          }),
      }),
      {
        name: STORE_NAME,
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({
          locationsByOrg: state.locationsByOrg,
        }),
      },
    ),
  )

const sortLocations = (locations: SharepointSavedLocation[]) =>
  [...locations].sort((left, right) => right.lastVisitedAt - left.lastVisitedAt)

export const useSharepointRecentLocations = (orgId: UUID | '') =>
  useSharepointLocationHistoryStore(
    useShallow((state) => {
      const orgKey = getOrganizationKey(orgId)
      return sortLocations(state.locationsByOrg[orgKey] ?? []).slice(0, 8)
    }),
  )

export const useSharepointPinnedLocations = (orgId: UUID | '') =>
  useSharepointLocationHistoryStore(
    useShallow((state) => {
      const orgKey = getOrganizationKey(orgId)
      return sortLocations(
        (state.locationsByOrg[orgKey] ?? []).filter(
          (location) => location.type === 'site' && location.pinned,
        ),
      )
    }),
  )

export const useSharepointRecordSiteVisit = () =>
  useSharepointLocationHistoryStore((state) => state.recordSiteVisit)

export const useSharepointRecordDriveVisit = () =>
  useSharepointLocationHistoryStore((state) => state.recordDriveVisit)

export const useSharepointTogglePinnedLocation = () =>
  useSharepointLocationHistoryStore((state) => state.togglePinnedLocation)

export { createDriveLocation, createSiteLocation }
